<template>
  <StackPageLayout @close="handleClose">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <PageTitle> Edit Categories</PageTitle>

        <RouterLink
          #default="{ navigate }"
          custom
          :to="{ name: 'settingsCategoryCreate' }"
        >
          <ButtonAction @click="navigate">
            Add Category
          </ButtonAction>
        </RouterLink>
      </div>
    </template>

    <div
      class="h-full overflow-hidden rounded-lg bg-background py-3"
    >
      <CategoryVirtualList>
        <template #category="item">
          <div class="flex">
            <RouterLink
              #default="{ navigate }"
              :to="{
                name: 'settingsCategoryEdit',
                params: { categoryId: item.category.id },
              }"
              custom
            >
              <div
                class="flex w-full cursor-pointer flex-row items-center space-x-3 p-3"
                @click="navigate"
              >
                <div
                  class="relative flex size-12 items-center justify-center rounded border border-background-fg/10 bg-primary font-semibold text-primary-fg"
                  :style="{
                    backgroundColor:
                      item.category.color || undefined,
                  }"
                >
                  {{ buildShortString(item.category.name) }}
                </div>
                <div
                  class="flex h-12 flex-1 flex-col items-start justify-center"
                >
                  <div
                    v-if="item.category.parentCategoryId"
                    class="mb-1 line-clamp-1 text-left text-xs font-semibold"
                  >
                    {{
                      trimNamePath(item.category.namePath)
                    }}
                  </div>
                  <div
                    class="line-clamp-1 text-left text-sm"
                  >
                    {{ item.category.name }}
                  </div>
                </div>
              </div>
            </RouterLink>
            <button
              type="button"
              class="flex w-8 items-center justify-center"
              @click="handleClickDots(item.category.id)"
            >
              <IconVerticalEllipsis class="size-6" />
            </button>
          </div>
        </template>
      </CategoryVirtualList>
    </div>
  </StackPageLayout>

  <BaseDrawer
    shrink
    :is-open="isEditDeleteDrawerOpen"
    @close="cancelEditDelete"
  >
    <div class="h-full overflow-y-auto px-3 pb-4">
      <ActionItemSmall
        label="Edit Category"
        :icon="IconEdit"
        @click="confirmEditDelete('edit')"
      />

      <ActionItemSmall
        label="Delete Category"
        :icon="IconTrash"
        @click="confirmEditDelete('delete')"
      />
    </div>
  </BaseDrawer>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    description="Are you sure you wish to delete this category?"
    confirm-label="Delete Category"
    cancel-label="No"
  />
</template>

<script setup lang="ts">
import {
  IconEdit,
  IconTrash,
  IconVerticalEllipsis,
} from "@/icons"

import { deleteCategory } from "@/services/categories/deleteCategory"

const router = useRouter()
const handleClose = () => router.push({ name: "moreMenu" })

const {
  isRevealed: isEditDeleteDrawerOpen,
  reveal: showEditDeleteDraw,
  confirm: confirmEditDelete,
  cancel: cancelEditDelete,
} = useConfirmDialog()
const handleClickDots = async (categoryId: string) => {
  const { data, isCanceled } = await showEditDeleteDraw()
  if (isCanceled) return

  if (data === "edit") {
    router.push({
      name: "settingsCategoryEdit",
      params: {
        categoryId: categoryId,
      },
    })
  } else if (data === "delete") {
    if (deleteConfirmDialogRef.value === null) return
    const { isCanceled: isDeleteCancelled } =
      await deleteConfirmDialogRef.value.reveal()
    if (isDeleteCancelled) return

    try {
      await deleteCategory(categoryId)
      toast.success("Category deleted!")
    } catch {
      toast.error("Failed to delete category")
    }
  }
}

const trimNamePath = (namePath: string) =>
  namePath
    .split(">")
    .map((x) => x.trim())
    .slice(0, -1)
    .join(" > ")

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)
</script>
